import React from "react";
import {
  ArrowLeft,
  Mail,
  Server,
  Shield,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
} from "lucide-react";
import { Link } from "react-router-dom";

export default function SMTPSetupGuide() {
  const [showPassword, setShowPassword] = React.useState(false);

  const commonProviders = [
    {
      name: "Gmail",
      smtp: { host: "smtp.gmail.com", port: "587" },
      imap: { host: "imap.gmail.com", port: "993" },
      note: "Use App Password, not your regular password",
    },
    {
      name: "Outlook/Hotmail",
      smtp: { host: "smtp-mail.outlook.com", port: "587" },
      imap: { host: "outlook.office365.com", port: "993" },
      note: "Works with @outlook.com, @hotmail.com, @live.com",
    },
    {
      name: "Yahoo Mail",
      smtp: { host: "smtp.mail.yahoo.com", port: "587" },
      imap: { host: "imap.mail.yahoo.com", port: "993" },
      note: 'Enable "Less secure app access" in settings',
    },
    {
      name: "Zoho Mail",
      smtp: { host: "smtppro.zoho.in", port: "465" },
      imap: { host: "imappro.zoho.in", port: "993" },
      note: "Professional email service",
    },
  ];

  return (
    <div className="min-h-screen bg-[#F0F6FF] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link to="/email-outreach">
            <button className="flex items-center gap-2 text-[#5E78F4] hover:text-[#4A63D2] mb-4">
              <ArrowLeft size={20} />
              Back to Dashboard
            </button>
          </Link>
          <h1 className="text-[32px] font-[700] text-[#2A2A2A] mb-2">
            SMTP Email Setup Guide
          </h1>
          <p className="text-[16px] text-[#666] leading-relaxed">
            Follow this step-by-step guide to connect your email account using
            SMTP/IMAP. This method works with most email providers and gives you
            full control over your email sending.
          </p>
        </div>

        {/* What is SMTP Section */}
        <div className="bg-white rounded-[20px] p-8 mb-8 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] rounded-[10px] flex items-center justify-center">
              <Server className="text-white" size={24} />
            </div>
            <h2 className="text-[24px] font-[600] text-[#2A2A2A]">
              What is SMTP?
            </h2>
          </div>

          <div className="space-y-4 text-[16px] text-[#666] leading-relaxed">
            <p>
              <strong>SMTP (Simple Mail Transfer Protocol)</strong> is the
              standard way to send emails. When you connect via SMTP, you're
              using your email provider's servers to send emails directly from
              your account.
            </p>
            <p>
              <strong>IMAP (Internet Message Access Protocol)</strong> allows us
              to read emails from your inbox, including replies to the emails
              you send. Together, SMTP + IMAP provide complete email
              functionality.
            </p>
          </div>

          <div className="mt-6 p-4 bg-[#F0F6FF] rounded-[10px] border border-[#E1EAFF]">
            <div className="flex items-start gap-3">
              <Shield className="text-[#5E78F4] mt-1" size={20} />
              <div>
                <h3 className="font-[600] text-[#2A2A2A] mb-2">
                  Security Note
                </h3>
                <p className="text-[14px] text-[#666]">
                  Your email credentials are stored securely and encrypted. We
                  only use them to send emails on your behalf and read replies.
                  Your credentials are never shared with third parties.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Step-by-Step Guide */}
        <div className="bg-white rounded-[20px] p-8 mb-8 shadow-sm">
          <h2 className="text-[24px] font-[600] text-[#2A2A2A] mb-6">
            Step-by-Step Setup
          </h2>

          {/* Step 1 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-[#5E78F4] text-white rounded-full flex items-center justify-center text-[14px] font-[600]">
                1
              </div>
              <h3 className="text-[20px] font-[600] text-[#2A2A2A]">
                Click "Connect Email Provider"
              </h3>
            </div>
            <p className="text-[16px] text-[#666] mb-4 ml-11">
              On the main dashboard, click the "Connect Email Provider" button
              to open the connection drawer.
            </p>
            <div className="ml-11 p-4 bg-[#F8F9FA] rounded-[10px] border border-[#DDD]">
              <img
                src="/attached_assets/generated_images/Email_dashboard_connect_button_4c53442e.png"
                alt="Dashboard with Connect Email Provider button highlighted"
                className="w-full rounded-[8px] shadow-sm"
              />
            </div>
          </div>

          {/* Step 2 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-[#5E78F4] text-white rounded-full flex items-center justify-center text-[14px] font-[600]">
                2
              </div>
              <h3 className="text-[20px] font-[600] text-[#2A2A2A]">
                Select "SMTP/IMAP Configuration"
              </h3>
            </div>
            <p className="text-[16px] text-[#666] mb-4 ml-11">
              In the provider selection drawer, choose "SMTP/IMAP Configuration"
              option.
            </p>
            <div className="ml-11 p-4 bg-[#F8F9FA] rounded-[10px] border border-[#DDD]">
              <img
                src="/attached_assets/generated_images/Provider_selection_drawer_interface_a5216dd5.png"
                alt="Provider selection with SMTP/IMAP option highlighted"
                className="w-full rounded-[8px] shadow-sm"
              />
            </div>
          </div>

          {/* Step 3 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-[#5E78F4] text-white rounded-full flex items-center justify-center text-[14px] font-[600]">
                3
              </div>
              <h3 className="text-[20px] font-[600] text-[#2A2A2A]">
                Fill in Your Email Details
              </h3>
            </div>
            <p className="text-[16px] text-[#666] mb-4 ml-11">
              Enter your email account information. Use the settings from the
              table below for common providers:
            </p>

            <div className="ml-11 space-y-4">
              <div className="p-4 bg-[#FFF9E6] border border-[#FFE066] rounded-[10px]">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-[#FF8C00] mt-1" size={20} />
                  <div>
                    <h4 className="font-[600] text-[#2A2A2A] mb-2">
                      Important: Use App Passwords
                    </h4>
                    <p className="text-[14px] text-[#666]">
                      For Gmail and most modern email providers, you need to use
                      an "App Password" instead of your regular password. See
                      the provider-specific instructions below.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-[#F8F9FA] rounded-[10px] border border-[#DDD]">
                <img
                  src="/attached_assets/generated_images/SMTP_configuration_form_interface_eba80c65.png"
                  alt="SMTP configuration form with fields filled"
                  className="w-full rounded-[8px] shadow-sm"
                />
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-[#5E78F4] text-white rounded-full flex items-center justify-center text-[14px] font-[600]">
                4
              </div>
              <h3 className="text-[20px] font-[600] text-[#2A2A2A]">
                Test Your Connection
              </h3>
            </div>
            <p className="text-[16px] text-[#666] mb-4 ml-11">
              Click "Verify Email Account" to test both SMTP (sending) and IMAP
              (receiving) connections.
            </p>
            <div className="ml-11 space-y-4">
              <div className="p-4 bg-[#F8F9FA] rounded-[10px] border border-[#DDD]">
                <img
                  src="/attached_assets/generated_images/Email_verification_success_message_59b561c4.png"
                  alt="Email verification success message"
                  className="w-full rounded-[8px] shadow-sm"
                />
              </div>

              <div className="p-4 bg-[#E8F5E8] border border-[#4CAF50] rounded-[10px]">
                <div className="flex items-center gap-3">
                  <CheckCircle className="text-[#4CAF50]" size={20} />
                  <div>
                    <h4 className="font-[600] text-[#2A2A2A]">Success!</h4>
                    <p className="text-[14px] text-[#666]">
                      Both SMTP and IMAP connections verified successfully.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Step 5 */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-[#5E78F4] text-white rounded-full flex items-center justify-center text-[14px] font-[600]">
                5
              </div>
              <h3 className="text-[20px] font-[600] text-[#2A2A2A]">
                Start Sending Emails
              </h3>
            </div>
            <p className="text-[16px] text-[#666] mb-4 ml-11">
              Once connected, your email account will appear on the dashboard.
              You can now send test emails and track replies.
            </p>
            <div className="ml-11 p-4 bg-[#F8F9FA] rounded-[10px] border border-[#DDD]">
              <img
                src="/attached_assets/generated_images/Connected_account_dashboard_analytics_17a3ca08.png"
                alt="Dashboard showing connected SMTP account with analytics"
                className="w-full rounded-[8px] shadow-sm"
              />
            </div>
          </div>
        </div>

        {/* Common Provider Settings */}
        <div className="bg-white rounded-[20px] p-8 mb-8 shadow-sm">
          <h2 className="text-[24px] font-[600] text-[#2A2A2A] mb-6">
            Common Email Provider Settings
          </h2>

          <div className="space-y-6">
            {commonProviders.map((provider, index) => (
              <div
                key={index}
                className="border border-[#E1E5E9] rounded-[10px] p-6"
              >
                <h3 className="text-[18px] font-[600] text-[#2A2A2A] mb-4">
                  {provider.name}
                </h3>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-[16px] font-[600] text-[#2A2A2A] mb-3">
                      SMTP Settings (Sending)
                    </h4>
                    <div className="space-y-2 text-[14px]">
                      <div className="flex justify-between">
                        <span className="text-[#666]">Host:</span>
                        <span className="font-[500] text-[#2A2A2A]">
                          {provider.smtp.host}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#666]">Port:</span>
                        <span className="font-[500] text-[#2A2A2A]">
                          {provider.smtp.port}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#666]">Security:</span>
                        <span className="font-[500] text-[#2A2A2A]">
                          STARTTLS
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-[16px] font-[600] text-[#2A2A2A] mb-3">
                      IMAP Settings (Receiving)
                    </h4>
                    <div className="space-y-2 text-[14px]">
                      <div className="flex justify-between">
                        <span className="text-[#666]">Host:</span>
                        <span className="font-[500] text-[#2A2A2A]">
                          {provider.imap.host}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#666]">Port:</span>
                        <span className="font-[500] text-[#2A2A2A]">
                          {provider.imap.port}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#666]">Security:</span>
                        <span className="font-[500] text-[#2A2A2A]">
                          SSL/TLS
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-[#F0F6FF] rounded-[8px]">
                  <p className="text-[14px] text-[#666]">
                    <strong>Note:</strong> {provider.note}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Gmail App Password Guide */}
        <div className="bg-white rounded-[20px] p-8 mb-8 shadow-sm">
          <h2 className="text-[24px] font-[600] text-[#2A2A2A] mb-6">
            How to Create Gmail App Password
          </h2>

          <div className="space-y-6">
            <div className="p-4 bg-[#FFF9E6] border border-[#FFE066] rounded-[10px]">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-[#FF8C00] mt-1" size={20} />
                <div>
                  <h4 className="font-[600] text-[#2A2A2A] mb-2">
                    Prerequisites
                  </h4>
                  <p className="text-[14px] text-[#666]">
                    You must have 2-Step Verification enabled on your Google
                    account to create App Passwords.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#5E78F4] text-white rounded-full flex items-center justify-center text-[14px] font-[600] mt-1">
                  1
                </div>
                <div>
                  <p className="text-[16px] text-[#2A2A2A] mb-2">
                    Go to your Google Account settings
                  </p>
                  <p className="text-[14px] text-[#666]">
                    Visit{" "}
                    <code className="bg-[#F5F5F5] px-2 py-1 rounded">
                      myaccount.google.com
                    </code>
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#5E78F4] text-white rounded-full flex items-center justify-center text-[14px] font-[600] mt-1">
                  2
                </div>
                <div>
                  <p className="text-[16px] text-[#2A2A2A] mb-2">
                    Go to Security → App passwords
                  </p>
                  <p className="text-[14px] text-[#666]">
                    Click "Security" in the left sidebar, then "App passwords"
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#5E78F4] text-white rounded-full flex items-center justify-center text-[14px] font-[600] mt-1">
                  3
                </div>
                <div>
                  <p className="text-[16px] text-[#2A2A2A] mb-2">
                    Create new app password
                  </p>
                  <p className="text-[14px] text-[#666]">
                    Enter "Email Outreach App" as the app name and click
                    "Create"
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-[#5E78F4] text-white rounded-full flex items-center justify-center text-[14px] font-[600] mt-1">
                  4
                </div>
                <div>
                  <p className="text-[16px] text-[#2A2A2A] mb-2">
                    Copy the generated password
                  </p>
                  <p className="text-[14px] text-[#666]">
                    Use this 16-character password in the SMTP configuration,
                    not your regular Gmail password
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="bg-white rounded-[20px] p-8 shadow-sm">
          <h2 className="text-[24px] font-[600] text-[#2A2A2A] mb-6">
            Troubleshooting
          </h2>

          <div className="space-y-6">
            <div className="border-l-4 border-[#FF6B6B] pl-4">
              <h3 className="text-[18px] font-[600] text-[#2A2A2A] mb-2">
                Connection Failed
              </h3>
              <ul className="text-[14px] text-[#666] space-y-1">
                <li>• Double-check your email and password</li>
                <li>
                  • For Gmail: Make sure you're using an App Password, not your
                  regular password
                </li>
                <li>• Verify the SMTP/IMAP server settings are correct</li>
                <li>
                  • Check if your email provider requires "Less secure app
                  access" to be enabled
                </li>
              </ul>
            </div>

            <div className="border-l-4 border-[#FFB800] pl-4">
              <h3 className="text-[18px] font-[600] text-[#2A2A2A] mb-2">
                Emails Not Sending
              </h3>
              <ul className="text-[14px] text-[#666] space-y-1">
                <li>• Verify your email account has sending permissions</li>
                <li>
                  • Check if you've reached your email provider's daily sending
                  limit
                </li>
                <li>• Make sure the "From Name" field is filled correctly</li>
              </ul>
            </div>

            <div className="border-l-4 border-[#4ECDC4] pl-4">
              <h3 className="text-[18px] font-[600] text-[#2A2A2A] mb-2">
                Not Receiving Replies
              </h3>
              <ul className="text-[14px] text-[#666] space-y-1">
                <li>• Check if IMAP settings are correct and verified</li>
                <li>• Replies might take a few minutes to appear</li>
                <li>• Verify the email provider supports IMAP access</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <Link to="/email-outreach">
            <button className="bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] text-white px-8 py-3 rounded-[10px] font-[600] hover:opacity-90 transition-all">
              Back to Dashboard
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}
